'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { ArrowRight, ClipboardCheck, FileText, LogIn, Search, Wrench } from 'lucide-react';

import { cn } from '@/lib/utils';
import { track } from '@/lib/analytics';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Eyebrow } from '@/components/custom/section-shell';

// The real backoffice repair states (Estados de seguimiento), faithful to the app.
const steps = [
  {
    name: 'Ingresado',
    desc: 'Ingresó al sistema, a la espera de diagnóstico.',
    date: 'Lun 3 nov',
    icon: LogIn,
    color: 'bg-indigo-500',
  },
  {
    name: 'Diagnosticando',
    desc: 'Diagnóstico técnico según el defecto manifestado.',
    date: 'Lun 3 nov',
    icon: ClipboardCheck,
    color: 'bg-orange-400',
  },
  {
    name: 'Informe generado',
    desc: 'Informe listo para aprobar o rechazar.',
    date: 'Mar 4 nov',
    icon: FileText,
    color: 'bg-yellow-400',
  },
  {
    name: 'En reparación',
    desc: 'Un técnico está reparando el equipo.',
    date: 'Mié 5 nov',
    icon: Wrench,
    color: 'bg-green-500',
  },
];

const TrackingFeature = () => {
  const [code, setCode] = useState('');
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // Draw the timeline in when the card scrolls into view (steps stagger,
  // rail segments grow). Idle = visible, so no-JS / reduced-motion see it whole.
  const listRef = useRef<HTMLOListElement>(null);
  const [phase, setPhase] = useState<'idle' | 'armed' | 'in'>('idle');

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const el = listRef.current;

    if (!el) return;

    setPhase('armed');

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setPhase('in');
          io.disconnect();
        }
      },
      { threshold: 0.3 },
    );

    io.observe(el);

    return () => io.disconnect();
  }, []);

  const handleSearch = () => {
    const params = new URLSearchParams(searchParams);
    const trimmed = code.trim();

    track('tracking_search', { has_code: Boolean(trimmed) });

    if (trimmed) {
      params.set('mt-code', trimmed);
    } else {
      params.delete('mt-code');
    }

    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  };

  return (
    <section
      id="seguimiento"
      className="relative bg-dark-ground py-[var(--section-y-sm)] text-dark-ground-ink md:py-[var(--section-y)]"
    >
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"
      />
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent"
      />
      <div className="mx-auto grid w-full max-w-[var(--container)] grid-cols-1 items-center gap-12 px-6 lg:grid-cols-2 lg:gap-16">
        {/* Value copy */}
        <div className="flex flex-col items-start gap-5">
          <Eyebrow tone="dark">El diferencial</Eyebrow>
          <h2 className="font-display text-h2 font-bold tracking-[-0.02em]">
            Tu cliente ve el estado de su reparación. Vos dejás de atender el teléfono.
          </h2>
          <p className="max-w-lg text-lead text-dark-ground-soft">
            Cada reparación tiene un link de seguimiento propio. El cliente entra con su código y ve
            en qué anda su equipo, en tiempo real. Menos llamados, más confianza.
          </p>

          {/* Customer code search — lives here, not in the hero */}
          <div className="mt-2 w-full max-w-md rounded-xl border border-white/10 bg-white/[0.03] p-5">
            <p className="mb-3 font-mono text-eyebrow uppercase tracking-[0.18em] text-dark-ground-soft">
              ¿Sos cliente de un taller?
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <Input
                id="codigo-seguimiento"
                type="text"
                placeholder="Código de seguimiento"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="h-11 w-full border-white/15 bg-transparent text-dark-ground-ink placeholder:text-dark-ground-soft focus-visible:border-brand-on-dark focus-visible:ring-brand-on-dark/40"
              />
              <Button variant="brand" onClick={handleSearch} className="h-11 w-full sm:w-auto">
                Buscar <Search />
              </Button>
            </div>
          </div>
        </div>

        {/* Tracking card — mirrors the real backoffice states */}
        <div className="w-full">
          <div className="rounded-2xl border border-dark-ground-line bg-white/[0.05] p-6 shadow-[var(--shadow-lg)] backdrop-blur-sm">
            <div className="mb-6">
              <p className="font-mono text-sm text-brand-lift">#MT-1042</p>
              <p className="text-sm text-dark-ground-soft">Televisor LED · Samsung</p>
            </div>

            <ol ref={listRef} className="flex flex-col">
              {steps.map((step, i) => {
                const hidden = phase === 'armed';
                const shown = phase === 'in';

                return (
                  <li key={step.name} className="flex gap-4">
                    {/* Rail */}
                    <div className="flex flex-col items-center">
                      <span
                        className={cn(
                          'flex size-9 items-center justify-center rounded-full text-white transition-all duration-700',
                          step.color,
                          hidden && 'scale-0 opacity-0',
                          shown && 'scale-100 opacity-100',
                        )}
                        style={{ transitionDelay: shown ? `${i * 0.18}s` : undefined }}
                      >
                        <step.icon className="size-4" />
                      </span>
                      {i < steps.length - 1 && (
                        <span
                          className={cn(
                            'my-1 w-px flex-1 origin-top bg-dark-ground-line transition-transform duration-700',
                            hidden && 'scale-y-0',
                            shown && 'scale-y-100',
                          )}
                          style={{ transitionDelay: shown ? `${i * 0.18 + 0.1}s` : undefined }}
                        />
                      )}
                    </div>
                    {/* Content */}
                    <div
                      className={cn(
                        'pb-5 transition-all duration-700',
                        hidden && 'translate-x-2 opacity-0',
                        shown && 'translate-x-0 opacity-100',
                      )}
                      style={{ transitionDelay: shown ? `${i * 0.18 + 0.06}s` : undefined }}
                    >
                      <p className="font-semibold text-dark-ground-ink">{step.name}</p>
                      <p className="text-sm text-dark-ground-soft">{step.desc}</p>
                      <p className="mt-1 font-mono text-xs text-dark-ground-soft/70">{step.date}</p>
                    </div>
                  </li>
                );
              })}
            </ol>

            <div className="mt-2 flex items-center gap-2 border-t border-dark-ground-line pt-4 text-sm text-dark-ground-soft">
              <ArrowRight className="size-4 text-brand-on-dark" />
              Así ve el seguimiento cada cliente de tu taller.
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrackingFeature;
